<script setup lang="ts">
//DataTable.vue
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/vue-table';
import type { FlatCampaign } from './flattenCampaigns';
import {
  FlexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  useVueTable,
} from '@tanstack/vue-table';
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { valueUpdater } from 'dashboard/helper/utils';
import { campaignStatusLabel } from 'dashboard/routes/dashboard/campaigns/helper/campaignStatus';

// Usando os componentes shadcn que você baixou
import {
  TableBody,
  TableCell,
  TableRow,
} from 'dashboard/components-shadcn/new-york/ui/table';
import DataTablePagination from './DataTablePagination.vue';
import DataTableToolbar from './DataTableToolbar.vue';
import RowRenderer from './RowRenderer.vue';
import CampaignStatisticsModal from './CampaignStatisticsModal.vue';

// Definir a interface de props
interface DataTableProps {
  columns: ColumnDef<FlatCampaign, any>[];
  data: FlatCampaign[];
  defaultExpanded?: boolean; // Controla se grupos iniciam expandidos
  serverSidePagination?: boolean; // Desabilita paginação interna quando usando server-side
  statusOptions?: { value: string; label: string }[]; // Opções de status para o filtro (server-side)
  initialDateFilter?: { startDate: string; endDate: string } | null; // Filtro de data inicial (server-side)
  isLoading?: boolean; // Estado de carregamento (server-side)
}

const props = withDefaults(defineProps<DataTableProps>(), {
  defaultExpanded: false,
  serverSidePagination: false,
  statusOptions: () => [],
  initialDateFilter: null,
  isLoading: false,
});

// Emits para server-side filtering
const emit = defineEmits<{
  globalFilterChange: [value: string]
  statusFilterChange: [value: string[]]
  dateFilterChange: [value: Date[] | undefined]
}>();

const { t } = useI18n();

const sorting = ref<SortingState>([]);
const columnFilters = ref<ColumnFiltersState>([]);
const columnVisibility = ref<VisibilityState>({});
const rowSelection = ref({});
const rowGrouping = ref<string[]>([]);
const globalFilter = ref(''); // ✅ Adicionar esta linha
const isProcessing = ref(false);

// Modal de estatisticas
const statsModalRef = ref(null);
const statsModalData = ref({
  campaignId: null,
  schedulingId: null,
  statistics: null,
  progressData: {},
  holdStats: null,
  status: '',
  statusMessage: ''
});

const handleOpenStatsModal = (event: Event) => {
  const customEvent = event as CustomEvent;
  statsModalData.value = customEvent.detail;
  nextTick(() => {
    statsModalRef.value?.open();
  });
};

// Estado para controlar paginação quando há agrupamento
const hasGrouping = computed(() => rowGrouping.value.length > 0);

// ✅ Função de filtro customizada para intervalo de datas
const dateRangeFilterFn = (row: any, columnId: string, filterValue: any) => {
  if (!filterValue || !Array.isArray(filterValue) || filterValue.length !== 2) {
    return true; // Se não há filtro, mostrar todas as linhas
  }

  const rowDate = new Date(row.getValue(columnId));
  const [startDate, endDate] = filterValue;

  // Verificar se a data da linha está dentro do intervalo
  return rowDate >= startDate && rowDate <= endDate;
};

// Extrair valores únicos de campaign_status_values
const campaignStatusValues = computed(() => {
  // Se server-side pagination e statusOptions fornecido, usar as opções do servidor
  if (props.serverSidePagination && props.statusOptions && props.statusOptions.length > 0) {
    return props.statusOptions;
  }

  // Fallback: extrair dos dados (client-side)
  const statusSet = new Set<string>();
  props.data.forEach(campaign => {
    if (campaign.campaign_status_values) {
      campaign.campaign_status_values.forEach(status => {
        if (typeof status === 'string') {
          statusSet.add(status);
        } else if (status && status.value) {
          statusSet.add(status.value);
        }
      });
    }
  });
  return Array.from(statusSet).map(status => ({
    value: status,
    // value é o enum CRU; label traduzido via i18n (status desconhecido volta cru)
    label: campaignStatusLabel(status, t),
  }));
});

const table = useVueTable({
  get data() { return props.data },
  get columns() { return props.columns },
  state: {
    get sorting() { return sorting.value },
    get columnFilters() { return columnFilters.value },
    get columnVisibility() { return columnVisibility.value },
    get rowSelection() { return rowSelection.value },
    get grouping() { return rowGrouping.value },
    get globalFilter() { return globalFilter.value },
  },
  enableRowSelection: true,
  enableGrouping: true,
  // Desabilitar filtragem/paginação client-side quando usar server-side
  get manualFiltering() { return props.serverSidePagination },
  get manualPagination() { return props.serverSidePagination },
  globalFilterFn: 'includesString',
  filterFns: {
    dateRange: dateRangeFilterFn,
  },
  onSortingChange: updaterOrValue => valueUpdater(updaterOrValue, sorting),
  onColumnFiltersChange: updaterOrValue => valueUpdater(updaterOrValue, columnFilters),
  onColumnVisibilityChange: updaterOrValue => valueUpdater(updaterOrValue, columnVisibility),
  onRowSelectionChange: updaterOrValue => valueUpdater(updaterOrValue, rowSelection),
  onGlobalFilterChange: updaterOrValue => valueUpdater(updaterOrValue, globalFilter),
  onGroupingChange: updaterOrValue => {
    // Resetar paginação quando alterar agrupamento
    table.setPageIndex(0);
    valueUpdater(updaterOrValue, rowGrouping);
  },
  getCoreRowModel: getCoreRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getFacetedRowModel: getFacetedRowModel(),
  getFacetedUniqueValues: getFacetedUniqueValues(),
  getGroupedRowModel: getGroupedRowModel(),
});

// Computed para estatísticas úteis
const tableStats = computed(() => {
  const rows = table.getRowModel().rows;
  const groupedRows = rows.filter(row => row.getIsGrouped());
  const expandedRows = groupedRows.filter(row => row.getIsExpanded());

  // Contar linhas de dados reais baseado no modelo filtrado
  const filteredRows = table.getFilteredRowModel().rows;
  const dataRows = filteredRows.filter(row => !row.getIsGrouped());

  return {
    totalRows: rows.length,
    totalDataRows: dataRows.length,
    totalFilteredRows: filteredRows.length,
    groupedRows: groupedRows.length,
    expandedRows: expandedRows.length,
    selectedRows: Object.keys(rowSelection.value).length,
    allGroupsExpanded: groupedRows.length > 0 && expandedRows.length === groupedRows.length,
    hasGrouping: hasGrouping.value,
  };
});

// Função recursiva otimizada para expandir/colapsar grupos
function toggleGroupedRows(rows: any[], expand: boolean, depth = 0) {
  rows.forEach(row => {
    if (row.getIsGrouped()) {
      if (row.getIsExpanded() !== expand) {
        row.toggleExpanded(expand);
      }
      if (row.subRows) {
        toggleGroupedRows(row.subRows, expand, depth + 1);
      }
    }
  });
}

// Funções públicas para controle externo
const expandAllGroups = async () => {
  if (isProcessing.value) return;
  isProcessing.value = true;

  try {

    await nextTick();
    toggleGroupedRows(table.getRowModel().rows, true);
  } finally {
    isProcessing.value = false;
  }
};

const collapseAllGroups = async () => {
  if (isProcessing.value) return;
  isProcessing.value = true;

  try {
    await nextTick();
    toggleGroupedRows(table.getRowModel().rows, false);
  } finally {
    isProcessing.value = false;
  }
};

// Função para expandir apenas primeiro nível
const expandFirstLevel = async () => {
  if (isProcessing.value) return;
  isProcessing.value = true;

  try {
    await nextTick();

    table.getRowModel().rows.forEach(row => {
      if (row.getIsGrouped() && !row.getIsExpanded()) {
        row.toggleExpanded(true);
      }
    });

  } finally {
    isProcessing.value = false;
  }
};

// Função para ajustar tamanho da página baseado no agrupamento
const adjustPageSizeForGrouping = () => {
  if (hasGrouping.value) {
    // Quando há agrupamento, aumentar o page size para acomodar melhor os grupos
    const currentPageSize = table.getState().pagination.pageSize;
    if (currentPageSize < 20) {
      table.setPageSize(10);
    }
  }
};

onMounted(() => {
  window.addEventListener('open-campaign-stats-modal', handleOpenStatsModal);

  // ✅ Se server-side pagination está ativo, mostrar todos os dados (já vêm paginados do servidor)
  if (props.serverSidePagination) {
    table.setPageSize(1000); // Valor alto para mostrar todos os dados da página atual do servidor
  }

  // Aplicar expansão inicial se necessário
  if (rowGrouping.value.length > 0) {
    adjustPageSizeForGrouping();
    nextTick(() => {
      if (props.defaultExpanded) {
        expandAllGroups();
      } else {
        expandFirstLevel();
      }
    });
  }
});


onUnmounted(() => {
  window.removeEventListener('open-campaign-stats-modal', handleOpenStatsModal);
});

// Watch otimizado para mudanças de agrupamento
watch(rowGrouping, async (newGrouping, oldGrouping) => {

  // Resetar página para a primeira quando alterar agrupamento
  table.setPageIndex(0);

  if (newGrouping.length > 0) {
    // Ajustar page size para melhor experiência com grupos
    adjustPageSizeForGrouping();

    // Aguarda a re-renderização da tabela
    await nextTick();

    if (props.defaultExpanded) {
      expandAllGroups();
    } else {
      expandFirstLevel();
    }
  }
}, { deep: true });

// Watch para mudanças nos dados
watch(() => props.data, (newData, oldData) => {

  // Resetar página quando dados mudam
  table.setPageIndex(0);

  // Re-aplicar expansão se houver agrupamento ativo
  if (rowGrouping.value.length > 0) {
    nextTick(() => {
      if (props.defaultExpanded) {
        expandAllGroups();
      } else {
        expandFirstLevel();
      }
    });
  }
}, { deep: true });

// Watch para server-side filtering - globalFilter
watch(globalFilter, (newValue) => {
  if (props.serverSidePagination) {
    emit('globalFilterChange', newValue || '');
  }
});

// Armazenar valores anteriores dos filtros para comparação
const previousStatusFilter = ref<string[] | undefined>(undefined);
const previousDateFilter = ref<Date[] | undefined>(undefined);

// Watch para server-side filtering - columnFilters
watch(columnFilters, (newFilters) => {
  if (!props.serverSidePagination) return;

  // Verificar mudança no filtro de status
  const statusFilter = newFilters.find(f => f.id === 'campaign_status');
  const currentStatusValue = (statusFilter?.value as string[]) || [];
  const prevStatusValue = previousStatusFilter.value || [];

  if (JSON.stringify(currentStatusValue) !== JSON.stringify(prevStatusValue)) {
    previousStatusFilter.value = [...currentStatusValue];
    emit('statusFilterChange', currentStatusValue);
  }

  // Verificar mudança no filtro de data
  const dateFilter = newFilters.find(f => f.id === 'scheduled_at');
  const currentDateValue = dateFilter?.value as Date[] | undefined;
  const dateChanged = JSON.stringify(currentDateValue) !== JSON.stringify(previousDateFilter.value);

  if (dateChanged) {
    previousDateFilter.value = currentDateValue ? [...currentDateValue] : undefined;
    emit('dateFilterChange', currentDateValue);
  }
}, { deep: true });

// Handler para repassar mudanças de data do DataTableToolbar para o componente pai
const handleDateRangeChange = (dates: Date[]) => {
  if (props.serverSidePagination) {
    emit('dateFilterChange', dates.length === 2 ? dates : undefined);
  }
};

// Expor funções e estados para uso externo
defineExpose({
  expandAllGroups,
  collapseAllGroups,
  expandFirstLevel,
  tableStats,
  isProcessing,
  table,
});
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 gap-4 px-3">
    <DataTableToolbar
      :table="table"
      :row-grouping="rowGrouping"
      :table-stats="tableStats"
      :is-processing="isProcessing"
      :campaign-status-values="campaignStatusValues"
      :server-side-pagination="serverSidePagination"
      :initial-date-filter="initialDateFilter"
      :is-loading="isLoading"
      @expand-all="expandAllGroups"
      @collapse-all="collapseAllGroups"
      @expand-first-level="expandFirstLevel"
      @date-range-change="handleDateRangeChange"
    />

    <!-- Table Container -->
    <div
      class="flex-1 min-h-0 rounded-md bg-white dark:bg-slate-800 shadow-sm border border-slate-200 dark:border-slate-800 overflow-auto"
    >
      <table class="whatsapp-table w-full min-w-[1000px] border-separate border-spacing-0 caption-bottom text-sm">
        <thead class="sticky top-0 z-10 bg-green-200 dark:bg-n-slate-2">
          <tr
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              class="font-semibold text-green-800 dark:text-green-300 p-4 text-left border-b border-green-900/30 dark:border-green-800"
            >
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </th>
          </tr>
        </thead>
        <TableBody class="data-table-body">
          <template v-if="table.getRowModel().rows?.length">
            <RowRenderer
              :rows="table.getRowModel().rows"
              :depth="0"
              :table="table"
              :row-grouping="rowGrouping"
              :is-expanding="isProcessing"
            />
          </template>
          <template v-else>
            <TableRow class="empty-row">
              <TableCell
                :colspan="props.columns.length"
                class="empty-cell"
              >
                <div class="empty-state">
                  <div class="empty-icon">📭</div>
                  <div class="empty-content">
                    <div class="empty-title">Nenhum resultado encontrado</div>
                    <div class="empty-subtitle">Tente ajustar os filtros ou remover agrupamentos</div>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          </template>
        </TableBody>
      </table>
    </div>

    <!-- Paginação com barra de status integrada (apenas se não for server-side) -->
      <DataTablePagination
        v-if="!serverSidePagination"
        :table="table"
        :table-stats="tableStats"
        :is-processing="isProcessing"
        class="data-table-pagination"
      />

    <!-- Modal de estatisticas -->
    <CampaignStatisticsModal
      ref="statsModalRef"
      :campaign-id="statsModalData.campaignId"
      :scheduling-id="statsModalData.schedulingId"
      :statistics="statsModalData.statistics"
      :progress-data="statsModalData.progressData"
      :hold-stats="statsModalData.holdStats"
      :status="statsModalData.status"
      :status-message="statsModalData.statusMessage"
    />
  </div>
</template>

<style scoped>

/* Table Styling */

/* Bordas finas para as linhas da tabela */
:deep(.whatsapp-table tbody tr td) {
  @apply p-3 border-b border-green-900/30 dark:border-green-900/30;
}

:deep(.whatsapp-table tbody tr:hover) {
  @apply bg-slate-50 dark:bg-green-900/10;
}

:deep(.whatsapp-table tbody) {
  @apply bg-white dark:bg-slate-900/20;
}

/* ✅ CORREÇÃO: Estilos para o estado vazio */
.empty-row {
  @apply hover:bg-transparent !important;
}

.empty-cell {
  @apply text-center py-12 !important;
}

.empty-state {
  @apply flex flex-col items-center justify-center gap-4;
}

.empty-icon {
  @apply text-4xl mb-2;
}

.empty-content {
  @apply flex flex-col items-center gap-2;
}

.empty-title {
  @apply text-lg font-semibold text-slate-700 dark:text-slate-300;
}

.empty-subtitle {
  @apply text-sm text-slate-500 dark:text-slate-400 max-w-md text-center;
}

</style>